// src/components/ProcessingResults/ImageStage.jsx
import React, { useEffect, useState, useRef } from 'react';
import { Stage, Layer, Image as KonvaImage } from 'react-konva';
import { Table, Spinner, Alert, Modal, Button } from 'react-bootstrap';

const ImageStage = ({
  jobData,
  originalImageRef,
  onStatusUpdate,
  onImageLoaded,
  isLoaded,
  externalShowInfo,
  onInfoClose
}) => {
  const [stageSize, setStageSize] = useState({ width: 800, height: 600 });
  const [displayImage, setDisplayImage] = useState(null);
  const [showInfoDialog, setShowInfoDialog] = useState(false);
  const processedRef = useRef(false);

  useEffect(() => {
    if (originalImageRef.current && !processedRef.current) {
      processedRef.current = true;
      processImage();
    }
  }, [originalImageRef.current]);

  const processImage = () => {
    const originalImage = originalImageRef.current;
    setDisplayImage(originalImage);
    calculateStageSize(originalImage);
    onImageLoaded();
    onStatusUpdate(`Original image loaded: W${originalImage.width}xH${originalImage.height}`);
  };

  const calculateStageSize = (img) => {
    const maxWidth = 800;
    const maxHeight = 600;
    const scale = Math.min(maxWidth / img.width, maxHeight / img.height, 1);

    setStageSize({
      width: img.width * scale,
      height: img.height * scale
    });
  };

  useEffect(() => {
      if (externalShowInfo !== undefined) {
        setShowInfoDialog(externalShowInfo);
      }
    }, [externalShowInfo]);

  const getImageTypeDisplay = (type) => {
    const types = {
      0: 'Camag_254',
      1: 'Camag_366',
      2: 'Camag_vis',
      3: 'Camera_vis',
      4: 'IR'
    };
    return types[type] || 'Unknown';
  };

  const getCropTypeDisplay = (type) => {
    const types = {
      0: 'Bottom', 1: 'Bottom_left', 2: 'Bottom_right',
      3: 'Left', 4: 'Right', 5: 'Top',
      6: 'Top_left', 7: 'Top_right', 8: 'Uncropped'
    };
    return types[type] || 'Unknown';
  };

  const getOrientationDisplay = (orientation) => {
    return orientation === 0 ? 'Horizontal' : 'Vertical';
  };

  const handleShowInfo = () => {
    setShowInfoDialog(true);

  };

  const handleCloseInfo = () => {
    onInfoClose();
    setShowInfoDialog(false);
  };

  return (
    <div className="image-stage">
      <div className="row">
        <div className="col-md-8">
          <Stage width={stageSize.width} height={stageSize.height}>
            <Layer>
              {displayImage && (
                <KonvaImage
                  image={displayImage}
                  width={stageSize.width}
                  height={stageSize.height}
                />
              )}
            </Layer>
          </Stage>
        </div>
      </div>
      <Modal show={showInfoDialog} onHide={handleCloseInfo} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-info-square" />
            <span>Image Information</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row">
            <div className="col-6">
              <div className="mb-3">
                <strong>Filename:</strong>
                <div className="text-muted">{jobData.original_image_filename || 'Unknown'}</div>
              </div>

              <div className="mb-3">
                <strong>Dimensions:</strong>
                <div className="text-muted">
                  {jobData.original_image_width} × {jobData.original_image_height} pixels
                </div>
              </div>

              <div className="mb-3">
                <strong>Image Type:</strong>
                <div className="text-muted">{getImageTypeDisplay(jobData.image_type)}</div>
              </div>
            </div>

            <div className="col-6">
              <div className="mb-3">
                <strong>Crop Type:</strong>
                <div className="text-muted">{getCropTypeDisplay(jobData.crop_type)}</div>
              </div>

              <div className="mb-3">
                <strong>Orientation:</strong>
                <div className="text-muted">{getOrientationDisplay(jobData.orientation)}</div>
              </div>

              <div className="mb-3">
                <strong>Processing Time:</strong>
                <div className="text-muted">
                  {jobData.processing_time?.toFixed(2)} seconds
                </div>
              </div>
            </div>
          </div>

          {jobData.uploaded_at && (
            <div className="mt-3 pt-3 border-top">
              <strong>Uploaded:</strong>
              <div className="text-muted">
                {new Date(jobData.uploaded_at).toLocaleString()}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseInfo}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ImageStage;